import React, { useCallback, useState } from 'react';
import axiosClient from '@/utils/client';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import FileUploader, { type FileUpload } from '@/components/common/FileUploader';
import ProfessionalDialog from '@/components/common/professional-dialog';
import VisibilityControls, { type VisibilitySettings } from '@/components/common/visibility-controls';

interface UploadFileDialogProps {
  onClose: () => void;
  onCreate: (results: FileApiResults[]) => void;
  open: boolean;
  folderId?: string;
}

interface FileApiResults {
  error?: string;
  message: string;
  file: {
    id: string;
    name: string;
  };
}

export function UploadFileDialog({ open, onClose, onCreate, folderId }: UploadFileDialogProps): React.JSX.Element {
  const [fileUploads, setFileUploads] = useState<FileUpload[]>([]);
  const [visibilitySettings, setVisibilitySettings] = useState<VisibilitySettings>({
    isPublic: true, // Default to public instead of private
  });

  const uploadFileRequest = useMutation({
    mutationFn: async (formData: FormData): Promise<FileApiResults[]> => {
      try {
        const response = await axiosClient.post('/api/v1/files/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error: any) {
        const errorMessage =
          error.status === 403
            ? 'You are not authorized to upload this file'
            : 'An error occurred while uploading the file';
        throw new Error(errorMessage);
      }
    },
    onSuccess: (data) => {
      toast.success(`Successfully uploaded ${data.length} file${data.length > 1 ? 's' : ''}`);
      setFileUploads([]);
      onCreate(data);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload file(s)');
    },
  });

  const handleClose = useCallback(() => {
    setFileUploads([]);
    onClose();
  }, [onClose]);

  const handleUpload = async () => {
    if (fileUploads.length === 0) return;

    const uploadsInProgress = fileUploads.some((upload) => upload.status === 'uploading');
    if (uploadsInProgress) return;

    try {
      const uploadPromises = fileUploads.map(async (upload) => {
        if (upload.status === 'success') {
          const formData = new FormData();
          formData.append('file', upload.file);
          formData.append('folder_id', folderId || '');
          formData.append('visibility', visibilitySettings.isPublic ? 'public' : 'private');

          return uploadFileRequest.mutateAsync(formData);
        }
        return null;
      });

      const results = await Promise.all(uploadPromises);
      const successfulResults = results.filter((result): result is FileApiResults[] => result !== null).flat();

      if (successfulResults.length > 0) {
        toast.success(`Successfully uploaded ${successfulResults.length} file(s)`);
        onCreate(successfulResults);
        handleClose();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload files');
    }
  };

  const uploadsInProgress = fileUploads.some((upload) => upload.status === 'uploading');

  return (
    <ProfessionalDialog
      actions={
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <LoadingButton
            color="primary"
            disabled={fileUploads.length === 0 || uploadsInProgress}
            loading={uploadFileRequest.isPending || uploadsInProgress}
            onClick={handleUpload}
            variant="contained"
          >
            Upload Files
          </LoadingButton>
        </Box>
      }
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      open={open}
      subtitle="Upload files to your community documents"
      title="Upload Files"
      variant="elevated"
    >
      <Box sx={{ mt: 2 }}>
        <Stack spacing={3}>
          {/* File Upload */}
          <Box>
            <Typography gutterBottom sx={{ fontWeight: 600, mb: 2 }} variant="subtitle2">
              Select Files
            </Typography>
            <FileUploader
              allowedTypes={['ALL']}
              maxFiles={10}
              onUploadComplete={setFileUploads}
              showPreviews
              subtitle="Drag & drop files here, or click to browse. Supports all file types."
              title="Upload Files to Community"
            />
          </Box>

          {/* Visibility Settings */}
          <Box>
            <VisibilityControls
              onChange={setVisibilitySettings}
              showAdvanced={false}
              subtitle="Choose whether files are public to all community members or private to managers/admins"
              title="Visibility"
              value={visibilitySettings}
            />
          </Box>

          {/* Upload Progress */}
          {uploadsInProgress ? <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Uploading...</strong> Please wait while your files are being uploaded. You can close this dialog
                once the upload is complete.
              </Typography>
            </Alert> : null}

          {/* Error Display */}
          {/* The original code had an 'error' state, but it was not defined.
              Assuming it's meant to be a placeholder for a potential error message
              from the uploadFileRequest.onError or similar.
              For now, removing it as it's not defined. */}
        </Stack>
      </Box>
    </ProfessionalDialog>
  );
}
